import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Bell, Zap, Calendar, CalendarDays, BellOff, ChevronLeft, Check, Mail, Package, Tag, CalendarClock, MessageSquare, Search, Layers, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { updateNotificationPreferences, updateEmailPreferences } from '@/lib/api';

const EMAIL_TYPE_OPTIONS = [
  { key: 'offers_on_my_wanted', icon: Package, iconColor: 'text-orange-500', label: 'Angebote auf meine Gesuche', description: 'Wenn ein Verkäufer ein neues Angebot abgibt oder ein Deal bestätigt wird.' },
  { key: 'my_offer_status', icon: Tag, iconColor: 'text-emerald-500', label: 'Status meiner abgegebenen Angebote', description: 'Annahme, Ablehnung, Gegenangebot oder Bestätigung Ihrer Angebote.' },
  { key: 'weekly_offer_digest', icon: CalendarClock, iconColor: 'text-purple-500', label: 'Wöchentlicher Angebots-Überblick', description: 'Montags eine Sammel-E-Mail „X neue Angebote diese Woche".' },
  { key: 'new_messages', icon: MessageSquare, iconColor: 'text-blue-500', label: 'Neue Nachrichten', description: 'Chat-Nachrichten und neue Anfragen anderer Nutzer.' },
  { key: 'saved_search_alerts', icon: Search, iconColor: 'text-cyan-500', label: 'Treffer für gespeicherte Suchen', description: 'Neue Inserate, die zu Ihren gespeicherten Suchen passen.' },
  { key: 'matches_escalations', icon: Layers, iconColor: 'text-amber-500', label: 'Material-/Bedarfs-Matches & Eskalationen', description: 'Passende Materialien, Bedarfs-Matches und Eskalations-Hinweise.' },
];

const DEFAULT_EMAIL_PREFS = {
  offers_on_my_wanted: true,
  my_offer_status: true,
  weekly_offer_digest: true,
  new_messages: true,
  saved_search_alerts: true,
  matches_escalations: true,
};

const FREQUENCY_OPTIONS = [
  {
    value: 'realtime',
    icon: Zap,
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-50 dark:bg-orange-900/20',
    label: 'Echtzeit',
    description: 'Sie erhalten sofort eine E-Mail, wenn eine neue Benachrichtigung für Sie vorliegt.',
    badge: 'Empfohlen',
    badgeColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  },
  {
    value: 'daily',
    icon: Calendar,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50 dark:bg-blue-900/20',
    label: 'Täglich',
    description: 'Einmal täglich um 08:00 Uhr erhalten Sie eine Zusammenfassung aller neuen Benachrichtigungen.',
  },
  {
    value: 'weekly',
    icon: CalendarDays,
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-50 dark:bg-purple-900/20',
    label: 'Wöchentlich',
    description: 'Jeden Montag um 08:00 Uhr erhalten Sie eine wöchentliche Zusammenfassung.',
  },
  {
    value: 'never',
    icon: BellOff,
    iconColor: 'text-slate-400',
    iconBg: 'bg-slate-100 dark:bg-slate-800',
    label: 'Deaktiviert',
    description: 'Sie erhalten keine E-Mail-Benachrichtigungen. Benachrichtigungen sind weiterhin in der App sichtbar.',
  },
];

export default function NotificationSettingsPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState('never');
  const [emailPrefs, setEmailPrefs] = useState(DEFAULT_EMAIL_PREFS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.notification_frequency) {
      setSelected(user.notification_frequency);
    }
    if (user?.email_preferences) {
      setEmailPrefs({ ...DEFAULT_EMAIL_PREFS, ...user.email_preferences });
    }
  }, [user]);

  const togglePref = (key) => setEmailPrefs((prev) => ({ ...prev, [key]: !prev[key] }));

  const prefsChanged = EMAIL_TYPE_OPTIONS.some(
    (opt) => emailPrefs[opt.key] !== (user?.email_preferences?.[opt.key] ?? true)
  );
  const freqChanged = user?.notification_frequency !== selected;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (freqChanged) await updateNotificationPreferences(selected);
      if (prefsChanged) await updateEmailPreferences(emailPrefs);
      if (refreshUser) await refreshUser();
      toast.success('Benachrichtigungs-Einstellungen gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = freqChanged || prefsChanged;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back navigation */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="back-button"
        >
          <ChevronLeft className="h-4 w-4" />
          Zurück
        </button>

        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 rounded-xl bg-orange-100 dark:bg-orange-900/20">
            <Bell className="h-6 w-6 text-orange-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Benachrichtigungs-Einstellungen</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Wählen Sie, wie oft Sie per E-Mail über neue Aktivitäten informiert werden möchten.
            </p>
          </div>
        </div>

        {/* Frequency options */}
        <div className="mb-2 flex items-center gap-2">
          <Bell className="h-4 w-4 text-orange-500" />
          <h2 className="text-base font-semibold text-foreground">Wie oft möchten Sie E-Mails erhalten?</h2>
        </div>
        <div className="space-y-3 mb-8" data-testid="frequency-options">
          {FREQUENCY_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSelected(opt.value)}
                className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-150 ${
                  isSelected
                    ? 'border-orange-500 bg-orange-50/60 dark:bg-orange-900/10 shadow-sm'
                    : 'border-border hover:border-orange-200 dark:hover:border-orange-800 bg-card hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
                data-testid={`frequency-option-${opt.value}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`mt-0.5 p-2 rounded-lg ${opt.iconBg}`}>
                    <Icon className={`h-5 w-5 ${opt.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${isSelected ? 'text-orange-700 dark:text-orange-400' : 'text-foreground'}`}>
                        {opt.label}
                      </span>
                      {opt.badge && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${opt.badgeColor}`}>
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {opt.description}
                    </p>
                  </div>
                  <div className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-orange-500 bg-orange-500' : 'border-border'
                  }`}>
                    {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Email type toggles */}
        <div className="mb-2 flex items-center gap-2">
          <Mail className="h-4 w-4 text-orange-500" />
          <h2 className="text-base font-semibold text-foreground">Welche E-Mails möchten Sie erhalten?</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Aktivieren oder deaktivieren Sie einzelne E-Mail-Typen. Die oben gewählte Frequenz steuert das Sammel-Timing.
        </p>
        <div className="space-y-2.5 mb-8" data-testid="email-type-options">
          {EMAIL_TYPE_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isOn = !!emailPrefs[opt.key];
            return (
              <div
                key={opt.key}
                className="flex items-center gap-4 rounded-xl border border-border bg-card p-4"
                data-testid={`email-type-${opt.key}`}
              >
                <div className={`p-2 rounded-lg bg-muted/50 ${opt.iconColor}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground">{opt.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{opt.description}</p>
                </div>
                <Switch
                  checked={isOn}
                  onCheckedChange={() => togglePref(opt.key)}
                  data-testid={`email-toggle-${opt.key}`}
                  aria-label={opt.label}
                />
              </div>
            );
          })}
        </div>

        {/* Transactional note */}
        <Card className="mb-8 border-dashed bg-muted/30">
          <CardContent className="pt-4 pb-4 flex items-start gap-3">
            <ShieldCheck className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Immer aktiv:</strong> Transaktionale E-Mails wie Bestellbestätigungen,
              Rechnungen und Abo-Status werden aus rechtlichen und betrieblichen Gründen immer versendet und können nicht abgeschaltet werden.
            </p>
          </CardContent>
        </Card>

        {/* Info card */}
        <Card className="mb-8 border-dashed bg-muted/30">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Hinweis:</strong> Benachrichtigungen erscheinen immer in der App im Glocken-Symbol — unabhängig von den E-Mail-Einstellungen. 
              Die E-Mail wird an <strong className="text-foreground">{user?.email}</strong> gesendet.
            </p>
          </CardContent>
        </Card>

        {/* Save button */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={!hasChanged || saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="save-notification-preferences-btn"
          >
            {saving ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Speichern...</>
            ) : (
              <><Check className="h-4 w-4 mr-2" /> Einstellungen speichern</>
            )}
          </Button>
          {!hasChanged && (
            <span className="text-xs text-muted-foreground">Keine Änderungen</span>
          )}
        </div>
      </div>
    </div>
  );
}
